import { TimeRange } from 'models'
import { useGetStatsTimeRange } from '../store'
import { useTranslation } from 'react-i18next'
import prettyBytes from 'pretty-bytes'
import moment from 'moment/moment'

type TimeRangeItem = {
  from: string
  to: string
}

type StatItem = {
  requested_at: string | number
  [key: string]: unknown
}

export const filterStatsByTimeRange = (stats: StatItem[], from: string, to: string): StatItem[] => {
  return stats.filter(({requested_at}) => {
    const requestTime = moment.unix(Number(requested_at) / 1000)
    const fromMoment = moment(from)
    const toMoment = moment(to)

    return (requestTime.isAfter(fromMoment) || requestTime.isSame(fromMoment)) && requestTime.isBefore(toMoment)
  })
}

type GenerateRangesParams = {
  startsAt: string | Date | moment.Moment
  endsAt: string | Date | moment.Moment
  scale: moment.unitOfTime.DurationConstructor
}

export const generateRanges = ({startsAt, endsAt, scale}: GenerateRangesParams): TimeRangeItem[] => {
  const start = moment(startsAt).startOf(scale)
  const end = moment(endsAt).add(scale === 'day' ? -1 : 0, 'day').endOf(scale)
  const ranges: TimeRangeItem[] = []
  const monthsDiff = end.diff(start, scale)
  for (let i = 0; i <= monthsDiff; i++) {
    const rangeStart = moment(start).add(i, scale).format('YYYY-MM-DD')
    const rangeEnd = moment(start).add(i + 1, scale).format('YYYY-MM-DD')
    ranges.push({
      from: rangeStart,
      to: rangeEnd
    })
  }

  return ranges
}

const useUtils = () => {
  const [selectedTimeRange] = useGetStatsTimeRange()
  const { i18n } = useTranslation()

  const customTickFormatter = (tick: string): string => {
    const date = new Date(Number.parseInt(tick))

    if (
      [
        TimeRange.Today,
        TimeRange.LastSevenDays,
        TimeRange.LastFourteenDays,
        TimeRange.LastThirtyDays,
        TimeRange.LastMonth,
      ].includes(selectedTimeRange)
    ) {
      return date.toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'numeric',
      })
    } else if (
      [
        TimeRange.LastThreeMonths, 
        TimeRange.LastSixMonths, 
        TimeRange.LastTwelveMonths,
        TimeRange.LastYear
      ].includes(selectedTimeRange)
    ) {
      return date.toLocaleDateString(i18n.language, { month: 'short' })
    } else {
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
      })
    }
  }

  const customDataUsageTickFormatter = (tick: string) => {
    const formatted = prettyBytes(Number.parseFloat(tick) * 1_000_000, {
      locale: i18n.language,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).replace('B', 'o').replace('GB', 'Go').replace('Gb', 'Go')
    
    return formatted
  }

  return {
    customTickFormatter,
    customDataUsageTickFormatter,
  }
}

export default useUtils
